/**
 * Sync-Zustand – was zwischen Geräten abgeglichen wird, und wie zwei Stände
 * zusammengeführt werden. Reine Funktionen ohne Speicher- oder Netzzugriff.
 *
 * Regeln (siehe doc/sync.md):
 *   - Sitzungen: Pro Sitzung gewinnt das jüngere `updatedAt`, auch wenn eine
 *     Seite eine Löschmarkierung ist. `lastOpenedAt` wird als Maximum übernommen.
 *     Abgelaufene Löschmarkierungen fallen weg, auch aus dem Serverstand.
 *   - Einstellungen: Pro Einstellung gewinnt die jüngere Änderung.
 *   - Zeichenzähler: Jedes Gerät schreibt nur seinen eigenen Zähler, also ist
 *     das Maximum je Eintrag immer richtig.
 */

import { TOMBSTONE_DAYS } from '../sessionManager.js';
import { mergeCounters } from '../usageTracker.js';

const TOMBSTONE_MS = TOMBSTONE_DAYS * 24 * 60 * 60 * 1000;

/** Zustandsformat; bei inkompatiblen Änderungen hochzählen */
export const STATE_FORMAT = 1;

/**
 * Einstellungen, die zwischen Geräten wandern. Lautstärke und Format bleiben
 * am Gerät.
 */
export const SYNCED_SETTINGS = [
  'apiKey',
  'languageCode',
  'voiceName',
  'speakingRate',
  'pitch',
  'autoplay',
  'retentionDays',
  'textSize',
];

/**
 * @typedef {import('../sessionManager.js').Session} Session
 * @typedef {{ values: Object<string, unknown>, updatedAt: Object<string, number> }} SyncedSettings
 * @typedef {Object<string, Object<string, Object<string, number>>>} UsageCounters
 * @typedef {{ format: number, sessions: Session[], settings: SyncedSettings, usage: UsageCounters }} SyncState
 */

/**
 * Führt den Stand dieses Geräts mit dem des Servers zusammen
 * @param {SyncState} local
 * @param {SyncState|null} remote - null, solange der Bereich leer ist
 * @returns {SyncState}
 */
export function mergeState(local, remote, now = Date.now()) {
  if (!remote) return local;
  return {
    format: STATE_FORMAT,
    sessions: mergeSessions(local.sessions, remote.sessions, now),
    settings: mergeSettings(local.settings, remote.settings),
    usage: mergeUsage(local.usage, remote.usage),
  };
}

/**
 * @param {Session[]} local
 * @param {Session[]} remote
 * @param {number} [now]
 * @returns {Session[]} nach id sortiert
 */
export function mergeSessions(local, remote, now = Date.now()) {
  const merged = new Map(local.map((session) => [session.id, session]));
  for (const theirs of remote) {
    const mine = merged.get(theirs.id);
    merged.set(theirs.id, mine ? newerSession(mine, theirs) : theirs);
  }
  return [...merged.values()]
    .filter((session) => !session.deletedAt || session.deletedAt > now - TOMBSTONE_MS)
    .sort((a, b) => compareCodeUnits(a.id, b.id));
}

/**
 * @param {Session} mine
 * @param {Session} theirs
 * @returns {Session}
 */
function newerSession(mine, theirs) {
  const winner = newer(mine.updatedAt, mine, theirs.updatedAt, theirs);
  if (winner.deletedAt) return winner;
  const lastOpenedAt = Math.max(mine.lastOpenedAt ?? 0, theirs.lastOpenedAt ?? 0);
  return { ...winner, lastOpenedAt };
}

/**
 * @param {SyncedSettings} local
 * @param {SyncedSettings} remote
 * @returns {SyncedSettings}
 */
export function mergeSettings(local, remote) {
  const merged = { values: {}, updatedAt: {} };
  for (const key of SYNCED_SETTINGS) {
    const localTime = local.updatedAt[key] ?? 0;
    const remoteTime = remote.updatedAt[key] ?? 0;
    const source = newer(localTime, local, remoteTime, remote, (side) => side.values[key]);
    if (!(key in source.values)) continue;
    merged.values[key] = source.values[key];
    merged.updatedAt[key] = Math.max(localTime, remoteTime);
  }
  return merged;
}

/**
 * Wählt die jüngere Seite. Bei gleichem Zeitpunkt entscheidet der Inhalt nach
 * einer festen Regel – so kommen alle Geräte zum selben Ergebnis, statt sich
 * gegenseitig zu überschreiben.
 * @template T
 * @param {number} mineTime
 * @param {T} mine
 * @param {number} theirsTime
 * @param {T} theirs
 * @param {(side: T) => unknown} [content] - der verglichene Inhalt
 * @returns {T}
 */
function newer(mineTime, mine, theirsTime, theirs, content = (side) => side) {
  if (mineTime !== theirsTime) return theirsTime > mineTime ? theirs : mine;
  return canonical(content(theirs)) > canonical(content(mine)) ? theirs : mine;
}

/**
 * @param {UsageCounters} local
 * @param {UsageCounters} remote
 * @returns {UsageCounters}
 */
export function mergeUsage(local, remote) {
  return mergeCounters(local, remote);
}

/**
 * Ob zwei Stände inhaltlich gleich sind – dann muss nichts hochgeladen werden
 * @param {SyncState} a
 * @param {SyncState|null} b
 * @returns {boolean}
 */
export function sameState(a, b) {
  return b !== null && isSame(a, b);
}

/**
 * Inhaltlicher Vergleich, bei dem die Reihenfolge der Schlüssel keine Rolle spielt
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function isSame(a, b) {
  return canonical(a) === canonical(b);
}

/** JSON mit sortierten Schlüsseln */
function canonical(value) {
  return JSON.stringify(value, (_, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([x], [y]) => compareCodeUnits(x, y)))
      : v
  );
}

/**
 * Vergleicht Zeichen für Zeichen. Nicht localeCompare: Das hängt von der
 * Spracheinstellung ab, und Geräte mit verschiedener Sprache kämen sonst zu
 * verschiedenen Ergebnissen und überschrieben sich gegenseitig.
 */
function compareCodeUnits(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
